import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import styled from 'styled-components';
import Radio from '../../../atoms/Radio';
import {
  isHalfWidth,
  toJapaneseBankingSystemHalfWidth,
} from '~/javascripts/admin/utils/characters/toHalfWidth';
import toKatakana from '~/javascripts/admin/utils/characters/toKatakana';
import type { PrivateMoneyFormValues } from '..';
import FormErrorMessage from '../../../atoms/FormErrorMessage';
import Grid from '../../../atoms/Grid';
import Label from '../../../atoms/Label';
import LocalizedMask from '../../../atoms/LocalizedMask';
import RangeInput from '../../../atoms/RangeInput';
import Required from '../../../atoms/Required';
import TextArea from '../../../atoms/TextArea';
import TextBox from '../../../atoms/TextBox';
import ToggleSwitch from '../../../atoms/ToggleSwitch';
import Tooltip from '../../../atoms/Tooltip';

const FormGrid = styled(Grid.Container)`
  ${Label} {
    margin-bottom: 0;
    padding: 5px 0;
    font-weight: bold;
    color: #676767;
  }

  ${ToggleSwitch} {
    display: block !important;
    margin: 6px 0;
  }

  ${Grid.Divider} {
    margin: 12px 0;
  }
`;

const Prefixed = styled.span`
  position: relative;
`;

const Prefix = styled.span`
  position: absolute;
  right: 100%;
  top: 50%;
  transform: translateY(-50%);
  padding: 0 8px;
`;

const ToggleSwitchLabel = styled(Label)`
  margin-left: 1.25rem;
  color: #aaaaaa !important;
`;

const RadioGroup = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 8px;
`;

const RadioLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 4px;
  cursor: pointer;
`;

const parseToNumber = (value: string, defaultValue: number | null | undefined = null) => {
  const parsedValue = Number.parseInt(value, 10);
  if (Number.isNaN(parsedValue)) return defaultValue;
  return parsedValue;
};

const ChargingMethodForm = ({
  allowedInputs = 'all',
}: {
  allowedInputs?: 'all' | (keyof PrivateMoneyFormValues)[];
}) => {
  const { register, control, watch } = useFormContext<PrivateMoneyFormValues>();

  const isInputAllowed = (name: keyof PrivateMoneyFormValues) =>
    allowedInputs === 'all' || allowedInputs?.includes(name);

  return (
    <FormGrid>
      <Grid.Item size={2}>
        <Label>クレジットカードチャージ</Label>
      </Grid.Item>
      <Grid.Item size={8} container gridSize={2} columnGap={32} rowGap={8}>
        <Grid.Item>
          <Controller
            name="can_use_credit_card"
            control={control}
            render={({ field: { onChange, value } }) => (
              <ToggleSwitch
                checked={value}
                onChange={onChange}
                disabled={!isInputAllowed('can_use_credit_card')}
                checkedText=""
                uncheckedText=""
              />
            )}
          />
          <ToggleSwitchLabel>クレジットカードチャージを有効にする</ToggleSwitchLabel>
        </Grid.Item>
        <Grid.Item nextRow flexDirection="column">
          <Label>1日あたりのマネーチャージ上限金額</Label>
          <Controller
            name="credit_card_daily_cap"
            control={control}
            rules={{
              validate: (value) => {
                if (!value) return true;
                if (
                  !!watch('credit_card_monthly_cap') &&
                  Number(value) > (watch('credit_card_monthly_cap') ?? 0)
                )
                  return '1ヶ月あたりのマネーチャージ上限金額を超えない金額を入力してください';
                return true;
              },
            }}
            render={({ field: { onChange, value } }) => (
              <LocalizedMask
                value={value?.toString() ?? ''}
                onChange={(e) => onChange(parseToNumber(e.target.value))}
              >
                {({ value: syncValue, ...inputProps }) => (
                  <TextBox
                    syncValue={syncValue}
                    fullWidth
                    min="1"
                    disabled={
                      !isInputAllowed('credit_card_daily_cap') || !watch('can_use_credit_card')
                    }
                    {...inputProps}
                  />
                )}
              </LocalizedMask>
            )}
          />
          <FormErrorMessage name="credit_card_daily_cap" />
        </Grid.Item>
        <Grid.Item nextRow flexDirection="column">
          <Label>1ヶ月あたりのマネーチャージ上限金額</Label>
          <Controller
            name="credit_card_monthly_cap"
            control={control}
            rules={{
              validate: (value) => {
                if (!value) return true;
              },
            }}
            render={({ field: { onChange, value } }) => (
              <LocalizedMask
                value={value?.toString() ?? ''}
                onChange={(e) => onChange(parseToNumber(e.target.value))}
              >
                {({ value: syncValue, ...inputProps }) => (
                  <TextBox
                    syncValue={syncValue}
                    fullWidth
                    min="1"
                    disabled={
                      !isInputAllowed('credit_card_monthly_cap') || !watch('can_use_credit_card')
                    }
                    {...inputProps}
                  />
                )}
              </LocalizedMask>
            )}
          />
          <FormErrorMessage name="credit_card_monthly_cap" />
        </Grid.Item>
        <Grid.Item nextRow flexDirection="column">
          <Label>
            チャージ額リスト
            <Tooltip id="id" text="カンマ区切りで数値を入力してください" />
          </Label>
          <Controller
            name="topup_method_amounts_for_credit_card"
            control={control}
            rules={{
              validate: (value) =>
                value.length === 0 ||
                /^[0-9,]+$/.test(value.join(',')) ||
                'カンマ区切りで数値を入力してください',
            }}
            render={({ field: { onChange, value } }) => (
              <TextArea
                rows={1}
                placeholder="例）1000,2000,3000,5000,10000"
                disabled={
                  !watch('can_use_credit_card') ||
                  !isInputAllowed('topup_method_amounts_for_credit_card')
                }
                value={value.join(',')}
                onChange={(e) => onChange(e.target.value ? e.target.value.split(',') : [])}
              />
            )}
          />
          <FormErrorMessage name="topup_method_amounts_for_credit_card" />
        </Grid.Item>
        <Grid.Item nextRow flexDirection="column">
          <Label>指定可能金額</Label>
          <Controller
            name="topup_method_amount_range_for_credit_card"
            control={control}
            render={({ field: { onChange, value } }) => (
              <RangeInput>
                <RangeInput.Field
                  type="number"
                  min="1"
                  value={value[0]?.toString() ?? ''}
                  onChange={(e) => onChange([e.target.value, value[1]])}
                  placeholder="下限値"
                  disabled={
                    !watch('can_use_credit_card') ||
                    !isInputAllowed('topup_method_amount_range_for_credit_card')
                  }
                />
                <RangeInput.Icon />
                <RangeInput.Field
                  type="number"
                  min="1"
                  value={value[1]?.toString() ?? ''}
                  onChange={(e) => onChange([value[0], e.target.value])}
                  placeholder="上限値"
                  disabled={
                    !watch('can_use_credit_card') ||
                    !isInputAllowed('topup_method_amount_range_for_credit_card')
                  }
                />
              </RangeInput>
            )}
          />
          <FormErrorMessage name="topup_method_amount_range_for_credit_card" />
        </Grid.Item>
        <Grid.Item nextRow flexDirection="column">
          <Label>チャレンジ認証要求</Label>
          <Controller
            name="require_3ds_challenge"
            control={control}
            render={({ field: { onChange, value } }) => (
              <RadioGroup>
                <RadioLabel>
                  <Radio
                    name="require_3ds_challenge"
                    checked={!value}
                    setChecked={() => onChange(false)}
                    disabled={
                      !watch('can_use_credit_card') ||
                      !isInputAllowed('require_3ds_challenge')
                    }
                  />
                  任意
                </RadioLabel>
                <RadioLabel>
                  <Radio
                    name="require_3ds_challenge"
                    checked={value}
                    setChecked={() => onChange(true)}
                    disabled={
                      !watch('can_use_credit_card') ||
                      !isInputAllowed('require_3ds_challenge')
                    }
                  />
                  要求する
                </RadioLabel>
              </RadioGroup>
            )}
          />
        </Grid.Item>
      </Grid.Item>

      <Grid.Divider />

      <Grid.Item size={2}>
        <Label>Bank Payチャージ</Label>
      </Grid.Item>
      <Grid.Item size={8} container gridSize={2} columnGap={32} rowGap={8}>
        <Grid.Item>
          <Controller
            name="can_use_bank_pay"
            control={control}
            render={({ field: { onChange, value } }) => (
              <ToggleSwitch
                checked={value}
                onChange={onChange}
                disabled={!isInputAllowed('can_use_bank_pay')}
                checkedText=""
                uncheckedText=""
              />
            )}
          />
          <ToggleSwitchLabel>Bank Payチャージを有効にする</ToggleSwitchLabel>
        </Grid.Item>
        <Grid.Item nextRow flexDirection="column">
          <Label>
            店舗コード <Required />
          </Label>
          <Controller
            control={control}
            name="bank_pay_store_code"
            rules={{
              required: watch('can_use_bank_pay') ? '店舗コードを入力してください' : false,
            }}
            render={({ field: { onChange, value } }) => (
              <TextBox
                placeholder="例）9999"
                disabled={!watch('can_use_bank_pay') || !isInputAllowed('can_use_bank_pay')}
                maxLength={32}
                fullWidth
                syncValue={value}
                onChange={(e) => e.target.value.match(/^[A-Za-z0-9]*$/) && onChange(e.target.value)}
              />
            )}
          />
          <FormErrorMessage name="bank_pay_store_code" />
        </Grid.Item>
        <Grid.Item nextRow flexDirection="column">
          <Label>
            イシュア区分（加盟店ID） <Required />
          </Label>
          <Prefixed>
            <Prefix>
              <Label>ISS</Label>
            </Prefix>
            <Controller
              control={control}
              name="bank_pay_iss_type"
              rules={{
                required: watch('can_use_bank_pay')
                  ? 'イシュア区分（加盟店ID）を入力してください'
                  : false,
              }}
              render={({ field: { onChange, value } }) => (
                <TextBox
                  placeholder="例）999"
                  disabled={!watch('can_use_bank_pay') || !isInputAllowed('bank_pay_iss_type')}
                  maxLength={3}
                  fullWidth
                  syncValue={value}
                  onChange={(e) =>
                    e.target.value.match(/^[A-Za-z0-9]*$/) && onChange(e.target.value)
                  }
                />
              )}
            />
          </Prefixed>
          <FormErrorMessage name="bank_pay_iss_type" />
        </Grid.Item>
        <Grid.Item nextRow flexDirection="column">
          <Label>
            通帳印字加盟店名 <Required />
          </Label>
          <Controller
            control={control}
            name="bank_pay_bankbook_merchant_name"
            rules={{
              required: watch('can_use_bank_pay') ? '通帳印字加盟店を入力してください' : false,
              validate: (value) =>
                isHalfWidth(value) || '通帳印字加盟店名には半角カナを入力してください',
            }}
            render={({ field: { onChange, value } }) => (
              <TextBox
                placeholder="例）ﾎﾟｹﾍﾟｲ"
                disabled={
                  !watch('can_use_bank_pay') || !isInputAllowed('bank_pay_bankbook_merchant_name')
                }
                maxLength={9}
                fullWidth
                syncValue={value}
                onChange={onChange}
                onBlur={(e) =>
                  onChange(
                    toJapaneseBankingSystemHalfWidth(toKatakana(e.target.value)).substring(0, 9)
                  )
                }
              />
            )}
          />
          <FormErrorMessage name="bank_pay_bankbook_merchant_name" />
        </Grid.Item>
        <Grid.Item nextRow flexDirection="column">
          <Label>Pay事業者ID</Label>
          <Controller
            control={control}
            name="bank_pay_p_id"
            render={({ field: { onChange, value } }) => (
              <TextBox
                placeholder="例）BP12345678"
                disabled={!watch('can_use_bank_pay') || !isInputAllowed('bank_pay_p_id')}
                maxLength={32}
                fullWidth
                syncValue={value}
                onChange={(e) => e.target.value.match(/^[A-Za-z0-9]*$/) && onChange(e.target.value)}
              />
            )}
          />
          <FormErrorMessage name="bank_pay_p_id" />
        </Grid.Item>
      </Grid.Item>

      <Grid.Divider />

      <Grid.Item size={2}>
        <Label>セブン銀行ATMチャージ</Label>
      </Grid.Item>
      <Grid.Item size={8} container gridSize={2} columnGap={32} rowGap={8}>
        <Grid.Item>
          <Controller
            name="can_use_seven_bank_atm"
            control={control}
            render={({ field: { onChange, value } }) => (
              <ToggleSwitch
                checked={value}
                onChange={onChange}
                disabled={!isInputAllowed('can_use_seven_bank_atm')}
                checkedText=""
                uncheckedText=""
              />
            )}
          />
          <ToggleSwitchLabel>セブン銀行ATMチャージを有効にする</ToggleSwitchLabel>
        </Grid.Item>
        <Grid.Item nextRow flexDirection="column">
          <Label>
            事業者コード <Required />
          </Label>
          <Controller
            name="seven_bank_issuer_code"
            control={control}
            rules={{
              required: watch('can_use_seven_bank_atm') ? '事業者コードを入力してください' : false,
              pattern: {
                value: /^[0-9]{6}$/,
                message: '6桁の数字を入力してください',
              },
            }}
            render={({ field: { onChange, value } }) => (
              <TextBox
                syncValue={value}
                onChange={(e) => (!Number.isNaN(Number(e.target.value)) ? onChange(e) : undefined)}
                maxLength={6}
                disabled={
                  !watch('can_use_seven_bank_atm') || !isInputAllowed('seven_bank_issuer_code')
                }
                fullWidth
              />
            )}
          />
          <FormErrorMessage name="seven_bank_issuer_code" />
        </Grid.Item>
        <Grid.Item nextRow flexDirection="column">
          <Label>
            事業者名 <Required />
          </Label>
          <TextBox
            {...register('seven_bank_issuer_name', {
              required: watch('can_use_seven_bank_atm') ? '事業者名を入力してください' : false,
            })}
            maxLength={15}
            disabled={!watch('can_use_seven_bank_atm') || !isInputAllowed('seven_bank_issuer_name')}
            fullWidth
          />
          <FormErrorMessage name="seven_bank_issuer_name" />
        </Grid.Item>
        <Grid.Item nextRow flexDirection="column">
          <Label>
            マネー名 <Required />
          </Label>
          <TextBox
            {...register('seven_bank_money_name', {
              required: watch('can_use_seven_bank_atm') ? 'マネー名を入力してください' : false,
            })}
            maxLength={15}
            disabled={!watch('can_use_seven_bank_atm') || !isInputAllowed('seven_bank_money_name')}
            fullWidth
          />
          <FormErrorMessage name="seven_bank_money_name" />
        </Grid.Item>
        <Grid.Item nextRow flexDirection="column">
          <Label>
            チャージ額リスト <Required />
          </Label>
          <Controller
            name="topup_method_amounts_for_seven_bank_atm"
            control={control}
            rules={{
              validate: (value) => {
                if (!watch('can_use_seven_bank_atm')) return true;

                if (value.length === 0) return 'チャージ額リストを入力してください';

                if (!/^[0-9,]+$/.test(value.join(','))) {
                  return '区切り文字はカンマを入力してください';
                }

                if (value.some((v) => Number(v) % 1000 !== 0)) {
                  return '数字は1000単位で入力してください';
                }

                if (value.some((v) => Number(v) > watch('money_topup_transfer_limit'))) {
                  return 'マネーチャージの上限額を超えています';
                }

                return true;
              },
            }}
            render={({ field: { onChange, value } }) => (
              <TextArea
                rows={1}
                placeholder="例）1000,2000,3000,5000,10000"
                disabled={
                  !watch('can_use_seven_bank_atm') ||
                  !isInputAllowed('topup_method_amounts_for_seven_bank_atm')
                }
                value={value.join(',')}
                onChange={(e) => onChange(e.target.value ? e.target.value.split(',') : [])}
              />
            )}
          />
          <FormErrorMessage name="topup_method_amounts_for_seven_bank_atm" />
        </Grid.Item>
      </Grid.Item>

      <Grid.Divider />
    </FormGrid>
  );
};

export default ChargingMethodForm;
